import React, { useEffect, useRef, useState } from 'react';

import { Button, Col, Drawer, Form, Input, Row, Select, Space, Divider, notification, Spin, Upload, message, Checkbox } from 'antd';
import ImgCrop from 'antd-img-crop';

import { editUser } from '../../../../../../Constants/Api/functions/function';
import { useSelector } from 'react-redux';

// Local image upload
import { UPLOAD_USER_IMAGE_URL } from '../../../../../../Constants/Api/azure/azure';

const { Option } = Select;

const EditOverview = ({ open, setOpen, user, getUser }) => {
    const formRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false)
    const [api, contextHolder] = notification.useNotification();
    const userProfile = useSelector(state => state.userProfile.userProfile)
    const [fileList, setFileList] = useState([]);
    const [isImageChanged, setIsImageChanged] = useState(false)
    const [isNotEmployed, setIsNotEmployed] = useState(false)

    useEffect(() => {
        if (user && formRef.current) {
            const notEmp = user.isEmployed === 0 || user.isEmployed === false;
            setIsNotEmployed(notEmp);
            formRef.current.setFieldsValue({
                name: user.name,
                address: user.address,
                contactNumber: user.contactNumber,
                role: user.role,
                notEmployed: notEmp,
                companyName: user.companyName || '',
                companyEmail: user.companyEmail || '',
                companyDesignation: user.companyDesignation || '',
                companyMobile: user.companyMobile || '',
                companyAddress: user.companyAddress || '',
            })

            setFileList(
                user?.profileImage ? [
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: user?.profileImage
                    },
                ]
                    :
                    []
            );
        }
    }, [user, formRef, open])

    const onFinish = async (values) => {
        const isNotEmp = Boolean(values.notEmployed);

        if (!isNotEmp && (user?.role === 'student' || values.role === 'student')) {
            if (
                !values.companyName?.trim() ||
                !values.companyEmail?.trim() ||
                !values.companyDesignation?.trim() ||
                !values.companyMobile?.trim() ||
                !values.companyAddress?.trim()
            ) {
                openNotificationWithIcon('error', 'All 5 company fields (Company Name, Email, Designation, Mobile, and Address) are required when marked as employed.');
                return;
            }
        }

        Object.keys(values).forEach(key => (values[key] === undefined || values[key] === '') && delete values[key]);
        values.id = user.id

        if (isNotEmp) {
            values.isEmployed = 0;
            values.companyName = null;
            values.companyEmail = null;
            values.companyDesignation = null;
            values.companyMobile = null;
            values.companyAddress = null;
        } else {
            values.isEmployed = 1;
            values.companyName = values.companyName?.trim() || null;
            values.companyEmail = values.companyEmail?.trim()?.toLowerCase() || null;
            values.companyDesignation = values.companyDesignation?.trim() || null;
            values.companyMobile = values.companyMobile?.trim() || null;
            values.companyAddress = values.companyAddress?.trim() || null;
        }
        values.isProfileCompleted = 1;
        delete values.notEmployed;

        setIsLoading(true)
        try {
            if (isImageChanged) {
                const uploadRes = await uploadFile(user.id)

                if (uploadRes && uploadRes.url) {
                    values.profileImage = uploadRes.url;
                }

                const response = await editUser(values)
                if (response && response.status === 200) {
                    openNotificationWithIcon('success')
                    setOpen(false)
                    getUser()
                }
            } else {
                const response = await editUser(values)
                if (response && response.status === 200) {
                    openNotificationWithIcon('success')
                    setOpen(false)
                    getUser()
                }
            }
        } catch (error) {
            openNotificationWithIcon('error', error)
        } finally {
            setIsLoading(false)
        }
    };

    const uploadFile = async (userId) => {
        try {
            const formData = new FormData();
            formData.append('image', fileList[0].originFileObj);
            formData.append('fileName', userId);

            const response = await fetch(UPLOAD_USER_IMAGE_URL, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Upload failed');
            }
            return data;
        } catch (error) {
            console.error('uploadFile error:', error);
            return null;
        }
    }

    const onClose = () => {
        setOpen(false);
    };

    const openNotificationWithIcon = (type, description) => {
        api[type]({
            message: type === 'success' ? 'Success' : 'Something went wrong',
            description: type === 'success' ? 'User profile updated successfully. Please refresh the page to see the changes'
                : `Error: ${description}`,
        });
    };

    const onChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
        setIsImageChanged(true)
    };
    const onPreview = async (file) => {
        let src = file.url;
        if (!src) {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);
                reader.onload = () => resolve(reader.result);
            });
        }
        const image = new Image();
        image.src = src;
        const imgWindow = window.open(src);
        imgWindow?.document.write(image.outerHTML);
    };

    return (
        <>
            {contextHolder}
            <Drawer
                title="Edit User Overview"
                width={720}
                onClose={onClose}
                open={open}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            form='edit-overview-form'
                            type="primary"
                            htmlType='submit'
                        >
                            Update
                        </Button>
                    </Space>
                }
            >
                <Spin
                    spinning={isLoading}
                    tip="Updating user profile..."
                >
                    <Form
                        id='edit-overview-form'
                        layout="vertical"
                        onFinish={onFinish}
                        ref={formRef}
                        requiredMark={true}
                    >
                        <Row
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <Col>
                                <Form.Item
                                    name="userImage"
                                    label="User Profile Image"
                                >
                                    <ImgCrop rotationSlider>
                                        <Upload
                                            action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188"
                                            listType="picture-card"
                                            fileList={fileList}
                                            onChange={onChange}
                                            onPreview={onPreview}
                                            beforeUpload={file => {
                                                const isPNG = (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg');
                                                if (!isPNG) {
                                                    message.error(`${file.name} is not a png file`);

                                                    return isPNG || Upload.LIST_IGNORE;
                                                } else {
                                                    return false;
                                                }
                                            }}
                                        >
                                            {fileList.length < 1 && '+ Upload'}
                                        </Upload>
                                    </ImgCrop>

                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="name"
                                    label="User Name"
                                >
                                    <Input placeholder="Please enter user name" />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name="contactNumber"
                                    label="Contact Number"
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || /^0[0-9]{9}$/.test(value)) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('The input is not a valid phone number (e.g., 0XX XXXX XXX)!'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input placeholder="0XX XXXX XXX" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            {(userProfile?.role === 'superadmin' || userProfile?.role === 'admin') &&
                                <Col span={12}>
                                    <Form.Item
                                        name="role"
                                        label="User Role"
                                    >
                                        <Select
                                            placeholder="Please select user role"
                                        >
                                            {userProfile?.role === 'superadmin' && (
                                                <Option value="superadmin">Super Admin</Option>
                                            )}
                                            <Option value="admin">Admin</Option>
                                            <Option value="coordinator">Coordinator</Option>
                                            <Option value="student">Student</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            }

                            <Col span={12}>
                                <Form.Item
                                    name="address"
                                    label="Address"
                                >
                                    <Input placeholder="Please enter user address" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider
                            orientation="left"
                            style={{
                                marginTop: '10px',
                                marginBottom: '20px',
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#1e293b'
                            }}
                        >
                            Company & Employment Details
                        </Divider>

                        <div style={{
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '10px',
                            padding: '12px 16px',
                            marginBottom: '16px',
                        }}>
                            <Form.Item name="notEmployed" valuePropName="checked" noStyle>
                                <Checkbox
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setIsNotEmployed(checked);
                                        if (checked) {
                                            formRef.current?.setFieldsValue({
                                                companyName: '',
                                                companyEmail: '',
                                                companyDesignation: '',
                                                companyMobile: '',
                                                companyAddress: '',
                                            });
                                        }
                                    }}
                                >
                                    <span style={{ fontWeight: 600, color: '#1e3a8a' }}>
                                        I am not currently employed / Full-time Student
                                    </span>
                                </Checkbox>
                            </Form.Item>
                            <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px', paddingLeft: '24px' }}>
                                Check this to set your profile as a full-time student. You can uncheck this box anytime later when you are employed to provide company details.
                            </div>
                        </div>

                        {isNotEmployed ? (
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginBottom: '16px',
                                color: '#475569',
                                fontSize: '13px',
                                lineHeight: '1.5'
                            }}>
                                🎓 Currently marked as a <strong>Full-Time Student</strong>. No company or workplace information is attached. Uncheck the box above whenever you want to add your company details.
                            </div>
                        ) : (
                            <>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="companyName"
                                            label="Company Name"
                                            rules={[
                                                { required: true, whitespace: true, message: 'Please enter company name' },
                                                { min: 2, message: 'Company name must be at least 2 characters' }
                                            ]}
                                        >
                                            <Input placeholder="e.g. Acme Corporation" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="companyEmail"
                                            label="Company Mail / Work Email"
                                            rules={[
                                                { required: true, whitespace: true, message: 'Please enter company email' },
                                                { type: 'email', message: 'Please enter a valid company email address' }
                                            ]}
                                        >
                                            <Input placeholder="e.g. name@company.com" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="companyDesignation"
                                            label="Company Designation"
                                            rules={[
                                                { required: true, whitespace: true, message: 'Please enter company designation' }
                                            ]}
                                        >
                                            <Input placeholder="e.g. Associate Engineer" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="companyMobile"
                                            label="Company Mobile No."
                                            rules={[
                                                { required: true, whitespace: true, message: 'Please enter company mobile number' },
                                                {
                                                    pattern: /^[0-9+()-\s]{7,20}$/,
                                                    message: 'Please enter a valid phone number'
                                                }
                                            ]}
                                        >
                                            <Input placeholder="e.g. 07X XXX XXXX" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item
                                            name="companyAddress"
                                            label="Company Address"
                                            rules={[
                                                { required: true, whitespace: true, message: 'Please enter company address' },
                                                { min: 5, message: 'Please enter a complete company address' }
                                            ]}
                                        >
                                            <Input.TextArea rows={2} placeholder="e.g. No. 123, Galle Road, Colombo" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </>
                        )}

                        <Divider
                            style={{
                                marginTop: '0px',
                                marginBottom: '15px'
                            }}
                        />
                    </Form>
                </Spin>
            </Drawer >
        </>
    );
};
export default EditOverview;